<template>
  <table
    v-if="order"
    class="order-items"
    aria-labelledby="cart-items-title"
  >
    <thead>
      <tr
        class="order-items__header"
        :class="{ 'sr-only': hideHeading }"
      >
        <th
          id="product-col"
          scope="col"
          colspan="2"
          class="text--left"
        >
          Artikel
        </th>
        <th
          v-if="!hideQuantity"
          id="quantity-col"
          scope="col"
          class="text--center"
        >
          Menge
        </th>
        <th
          id="total-price-col"
          scope="col"
          class="text--right hidden--xs"
        >
          Preis
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(item, key) in order?.items || []">
        <tr
          :key="key"
          class="order-items__row"
        >
          <td
            headers="product-col"
            class="order-items__column order-items__column-headline"
          >
            <strong class="text--mb">
              {{ item.name }}
            </strong>
          </td>
          <td class="order-items__column order-items__column-image">
            <GoliathImageElement
              v-if="goliathImage(item.teaserImage)"
              :image="goliathImage(item.teaserImage)"
              :alt="item.name"
              class="order-items__image"
              render-without-figure
            />
          </td>
          <td
            headers="product-col"
            class="order-items__column order-items__column-article"
          >
            <div class="text--mb text--bold hidden sm:block">
              {{ item.name }}
            </div>
            <div
              v-if="getProvider(item.provider) && !hideProvider"
              class="text--mb"
            >
              Anbieter: {{ getProvider(item.provider) }}
            </div>
            <div v-if="item.deliveryLeadTime && !hideDeliveryTime">
              Lieferzeit: {{ item.deliveryLeadTime }}
            </div>
          </td>
          <td
            v-if="!hideQuantity"
            headers="quantity-col"
            class="order-items__column order-items__column-quantity text--center"
            data-title="Menge:"
          >
            {{ item.quantity }}
          </td>
          <td
            headers="total-price-col"
            class="order-items__column order-items__column-price text--right"
            data-title="Preis:"
          >
            <Prices
              class="payment-process-item__price"
              :is-price="item.price ? humanizePriceFromCents(item.price) : undefined"
              :was-price="item.wasPrice ? humanizePriceFromCents(item.wasPrice) : undefined"
            />
          </td>
        </tr>
      </template>
    </tbody>
    <tfoot v-if="order.amount">
      <tr>
        <td
          colspan="4"
          class="order-items__column order-items__column-total"
        >
          <div class="order-items__column-total-">
            <strong>Gesamtsumme:</strong>
            <strong>{{ humanizePriceFromCents(order.amount) }}</strong>
          </div>
        </td>
      </tr>
    </tfoot>
  </table>
</template>
<script setup lang="ts">
import { Cart } from '~/lib/types/cartdata';
import { Order } from '~/lib/types/order';
import { type LocalImage, humanizePriceFromCents } from '@i22/rocket';
import { GoliathImageElement, Prices } from '@i22/td-longtail-vue-components';
import { getProvider } from '~/lib/providers';

export type OrderItemsProps = {
  order?: Cart | Order;
  hideQuantity?: boolean;
  hideProvider?: boolean;
  hideDeliveryTime?: boolean;
  hideHeading?: boolean;
};

const props = withDefaults(defineProps<OrderItemsProps>(), {
  order: undefined,
});

const goliathImage = (imagePath: string): LocalImage | undefined => {
  if (!imagePath?.startsWith('/public')) return undefined;
  const mediaId = imagePath.replace(/^\/public\/([^/]+)\/.*/, '$1');
  return {
    name: props.title,
    mediaId,
  };
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.order-items {
  width: 100%;
  border-collapse: collapse;

  &__header {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }

  &__row {
    border-top: 1px solid var(--color-stroke-subtle);

    @include mq(false, $mq-sm - 1) {
      display: grid;
      grid-template-areas: 'name name' 'image information' 'image quantity' 'image price';

      grid-template-columns: var(--order-item-image-xs-width, 80px) auto;
    }

    > td {
      padding-block: 0.5rem;
    }

    &:last-child {
      border-bottom: 1px solid var(--color-stroke-subtle);
    }
  }

  &__column-headline {
    grid-area: name;
    @include mq($mq-sm) {
      display: none;
    }
  }

  &__column-image {
    grid-area: image;
    padding-right: 1rem;

    @include mq($mq-sm) {
      width: calc(var(--image-width, 120px) + 1rem);
    }
  }

  &__image {
    width: 100%;
  }

  &__column-article {
    grid-area: information;
  }

  &__column-information {
    grid-area: information;
  }

  &__column-quantity,
  &__column-price {
    @include mq(false, $mq-sm - 1) {
      display: flex;
      justify-content: space-between;
      &:before {
        content: attr(data-title);
      }
    }
  }

  &__column-quantity {
    grid-area: quantity;
  }

  &__column-price {
    grid-area: price;
    @include mq($mq-sm) {
      padding-left: 0.5rem;
    }
  }

  :deep(.prices__main) {
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &__column-total {
    padding-top: 1rem;
    font-size: 1.2rem;

    > div {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
